
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Search } from 'lucide-react';
import { SupportTicket } from '@/utils/supportTicketService';
import { formatCentralTime } from '@/utils/supportTicketService';
import NewTicketDialog from './NewTicketDialog';

interface SupportTicketListProps {
  tickets: SupportTicket[];
  selectedTicketId?: string;
  onSelectTicket: (ticketId: string) => void;
  isLoading: boolean;
  userRole?: string;
}

const SupportTicketList: React.FC<SupportTicketListProps> = ({
  tickets,
  selectedTicketId,
  onSelectTicket,
  isLoading,
  userRole = 'user'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  // Filter tickets based on search term and status filter
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.caseNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || ticket.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'pending':
        return 'bg-amber-500 hover:bg-amber-600';
      case 'resolved':
        return 'bg-green-500 hover:bg-green-600';
      case 'closed':
        return 'bg-gray-500 hover:bg-gray-600';
      default:
        return 'bg-gray-500 hover:bg-gray-600';
    }
  };
  
  // Get priority indicator color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  return (
    <Card className="h-full">
      <CardHeader className="pb-3 space-y-4">
        <div className="flex justify-between items-center">
          <CardTitle>Tickets ({tickets.length})</CardTitle>
          {/* Only show new ticket button for appropriate roles */}
          {(userRole !== 'masterAdmin') && (
            <Button 
              size="sm" 
              onClick={() => setShowNewTicketDialog(true)}
              className="flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              New
            </Button>
          )}
        </div>
        
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tickets..."
            className="pl-8"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={statusFilter === 'all' ? 'default' : 'outline'} 
            onClick={() => setStatusFilter('all')}
            className="text-xs"
          >
            All
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'open' ? 'default' : 'outline'} 
            onClick={() => setStatusFilter('open')}
            className="text-xs"
          >
            Open
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'pending' ? 'default' : 'outline'} 
            onClick={() => setStatusFilter('pending')}
            className="text-xs"
          >
            Pending
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'resolved' ? 'default' : 'outline'} 
            onClick={() => setStatusFilter('resolved')}
            className="text-xs"
          >
            Resolved
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'closed' ? 'default' : 'outline'} 
            onClick={() => setStatusFilter('closed')}
            className="text-xs"
          >
            Closed
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        <ScrollArea className="h-[500px]">
          {isLoading ? (
            <div className="p-4 text-center">
              <p>Loading tickets...</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-muted-foreground">No tickets found</p>
              {searchTerm && (
                <Button 
                  variant="link" 
                  className="mt-2 text-sm" 
                  onClick={() => setSearchTerm('')}
                >
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredTickets.map(ticket => (
                <div
                  key={ticket.id}
                  className={`p-4 cursor-pointer transition-colors hover:bg-muted ${
                    selectedTicketId === ticket.id ? 'bg-muted' : ''
                  }`}
                  onClick={() => onSelectTicket(ticket.id)}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center">
                      <div 
                        className={`h-2 w-2 rounded-full mr-2 ${getPriorityColor(ticket.priority)}`} 
                        title={`Priority: ${ticket.priority}`}
                      />
                      <span className="font-medium text-sm line-clamp-1">
                        {ticket.subject}
                      </span>
                    </div>
                    <Badge 
                      variant="secondary"
                      className={`text-xs text-white ml-2 ${getStatusColor(ticket.status)}`}
                    >
                      {ticket.status}
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center text-xs text-muted-foreground mt-1">
                    <span>#{ticket.caseNumber}</span>
                    <span>{formatCentralTime(ticket.updatedAt)}</span>
                  </div>
                  
                  <div className="mt-1 text-xs text-muted-foreground flex justify-between">
                    <span>From: {ticket.userName}</span>
                    <span>Status: {ticket.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
      
      <NewTicketDialog
        open={showNewTicketDialog}
        onOpenChange={setShowNewTicketDialog}
        userRole={userRole}
        onTicketCreated={(ticket) => {
          setShowNewTicketDialog(false);
          onSelectTicket(ticket.id);
        }}
      />
    </Card>
  );
};

export default SupportTicketList;
